import { useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../config/api';
import AuthService from '../services/authService';

/**
 * Trip board state for the planner: the trip's owned board, its collections
 * (shared with the itinerary sidebar so it doesn't re-fetch the same
 * endpoint), and board creation. Extracted from useTripState — its only
 * coupling back into trip state is `tripName` for the default board name.
 *
 * The board is the trip's people's: the server answers only the owner and
 * accepted travellers (getTripPermissions), so a visitor on a public trip, a
 * pending invitee, or a viewer whose trip hasn't loaded yet asks nothing
 * (C368(8): each 403 was console noise). `isMember` is the trip GET's own
 * reading (tripEditRights); the fetch runs once it turns true.
 */
function useTripBoard(tripId, isNewTrip, tripName, isMember = false) {
  const [tripBoard, setTripBoard] = useState(null);
  const [boardCollections, setBoardCollections] = useState([]);
  const [isCreatingBoard, setIsCreatingBoard] = useState(false);

  const fetchTripBoard = useCallback(async (signal) => {
    if (isNewTrip || !tripId || !isMember) return;

    try {
      const response = await fetch(`${API_BASE}/api/trips/${tripId}/owned-board`, {
        headers: AuthService.getAuthHeaders(),
        signal
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.board) {
          const collectionsCount = data.collections?.length || 0;
          const itemsCount = data.collections?.reduce((sum, c) => sum + (c.item_count || 0), 0) || 0;
          setTripBoard({ ...data.board, collectionsCount, itemsCount });
          setBoardCollections(data.collections || []);
        } else {
          setTripBoard(null);
          setBoardCollections([]);
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Error fetching trip board:', err);
    }
  }, [tripId, isNewTrip, isMember]);

  const handleCreateTripBoard = useCallback(async () => {
    if (isNewTrip || !tripId || isCreatingBoard) return null;

    setIsCreatingBoard(true);
    try {
      const response = await fetch(`${API_BASE}/api/trips/${tripId}/owned-board`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({ name: `${tripName || 'Trip'} Board` })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.board) {
          setTripBoard({ ...data.board, collectionsCount: 0, itemsCount: 0 });
          // A fresh board has no collections; without this the sidebar kept
          // showing the pre-create list until the next tab-change refetch.
          setBoardCollections([]);
          return 'boards'; // Signal to switch to boards tab
        }
      } else {
        const errorData = await response.json();
        console.error('Failed to create board:', errorData);
      }
    } catch (err) {
      console.error('Error creating trip board:', err);
    } finally {
      setIsCreatingBoard(false);
    }
    return null;
  }, [tripId, isNewTrip, isCreatingBoard, tripName]);

  // Fetch when the trip loads; aborted on unmount so a fast back-navigation
  // can't setState on a dead tree. Someone no longer on the trip keeps no
  // copy of its board.
  useEffect(() => {
    if (!isNewTrip && tripId && isMember) {
      const controller = new AbortController();
      fetchTripBoard(controller.signal);
      return () => controller.abort();
    }
    setTripBoard(null);
    setBoardCollections(prev => (prev.length ? [] : prev));
    return undefined;
  }, [tripId, isNewTrip, isMember, fetchTripBoard]);

  return { tripBoard, boardCollections, isCreatingBoard, fetchTripBoard, handleCreateTripBoard };
}

export default useTripBoard;
